import { useEffect, useState } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";
import {
  HiOutlineChartBar,
  HiOutlineCog,
  HiOutlineHome,
  HiOutlineUserGroup,
  HiOutlineChevronRight,
  HiOutlineLogout,
  HiOutlineChartPie,
  HiOutlinePuzzle,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { Avatar } from "flowbite-react";
import { FiSidebar } from "react-icons/fi";
import cn from "classnames";

import { ROUTES } from "../../routes";
import { A } from "../A/A";
import { Logo, LogoVariants } from "../Logo/Logo";
import { CSMScopeFilter } from "../CSMScopeFilter";
import { useAuth } from "../../hooks";

export const Sidenav = () => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(
    localStorage.getItem("collapsed") === "true",
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const {
    logout,
    user,
    fetchUserDetails,
    userLoading,
    team,
    fetchTeamDetails,
  } = useAuth();

  useEffect(() => {
    fetchUserDetails();
    fetchTeamDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);

      // Auto-collapse on mobile, but respect user preference on desktop
      if (mobile) {
        setCollapsed(true);
        localStorage.setItem("collapsed", "true");
      } else {
        // On desktop, restore user preference
        const userPreference = localStorage.getItem("collapsed") === "true";
        setCollapsed(userPreference);
      }
    };

    // Set initial state based on screen size
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const routes = Object.values(ROUTES).map((route) => ({ path: route }));
  const [{ route: currentRoute }] = matchRoutes(routes, location);

  const NAV_LINKS_TOP = [
    {
      to: ROUTES.dashboard,
      icon: HiOutlineHome,
      label: t("navigation.dashboard"),
    },
    {
      to: ROUTES.customers,
      icon: HiOutlineUserGroup,
      label: t("navigation.customers"),
    },
    {
      to: ROUTES.forecast,
      icon: HiOutlineChartBar,
      label: t("navigation.forecast"),
    },
    {
      to: ROUTES.churn,
      icon: HiOutlineChartPie,
      label: t("navigation.churn"),
    },
  ];

  const NAV_LINKS_BOTTOM = [
    {
      to: ROUTES.settings,
      icon: HiOutlineCog,
      label: t("navigation.settings"),
    },
    {
      to: ROUTES.integrations,
      icon: HiOutlinePuzzle,
      label: "Integrations",
    },
    {
      to: ROUTES.dashboard,
      icon: HiOutlineLogout,
      label: t("navigation.logout"),
      onClick: () => logout(),
    },
  ];

  const toggleSidebar = () => {
    // Don't allow manual toggle on mobile - sidebar should stay collapsed
    if (isMobile) {
      return;
    }

    setCollapsed(!collapsed);
    localStorage.setItem("collapsed", !collapsed);
  };

  const expandSidebar = () => {
    if (collapsed) toggleSidebar();
  };

  const navClassName = cn(
    "flex h-screen flex-col justify-between border-r transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap bg-white dark:bg-gray-900 z-10 sticky top-0 left-0",
    {
      "w-16 min-w-16": collapsed,
      "w-64 min-w-64": !collapsed,
    },
  );

  const navItemIconClassName = cn("transition-all duration-300", {
    "opacity-70 mr-2": !collapsed,
    "opacity-100 mr-0": collapsed,
  });

  const navItemClassName = (isActive) =>
    cn(
      "flex items-center transition-all duration-300 overflow-hidden whitespace-nowrap",
      {
        "px-5 py-2.5": !collapsed,
        "px-5 py-2": collapsed,
        "text-blue-500": isActive,
      },
    );

  const logoWrapperClassName = cn(
    "flex flex-row items-center justify-between gap-2.5",
    {
      "px-5 py-5": !collapsed,
      "px-3 py-5": collapsed,
    },
  );

  const linkTextClassName = cn("transition-all duration-0", {
    "opacity-100 w-1": !collapsed,
    "opacity-0 absolute w-0": collapsed,
  });

  const renderToggleSidebarButton = () => {
    // Don't show toggle button on mobile
    if (isMobile) {
      return null;
    }

    return (
      <button onClick={toggleSidebar}>
        {collapsed ? (
          <HiOutlineChevronRight
            className="mb-3 ml-5 dark:text-white"
            size="20"
          />
        ) : (
          <FiSidebar size="18" className="dark:text-white" />
        )}
      </button>
    );
  };

  return (
    <nav aria-label="Sidebar with navigation" className={navClassName}>
      <div className="flex flex-col">
        <div className={logoWrapperClassName}>
          <div className="flex flex-row items-center gap-2.5">
            <div
              className={`flex h-10 ${collapsed ? "w-10" : "w-32"} items-center justify-center rounded-lg`}
            >
              <Logo
                variant={
                  !collapsed
                    ? LogoVariants.defaultWithText
                    : LogoVariants.default
                }
              />
            </div>
          </div>
          {!collapsed && renderToggleSidebarButton()}
        </div>
        {collapsed && renderToggleSidebarButton()}
        {/* Owner scope (PAN-425): above the page links so it reads as the container for
            everything below, and is visible on every page. */}
        <CSMScopeFilter
          collapsed={collapsed}
          canExpand={!isMobile}
          onExpand={expandSidebar}
        />
        {NAV_LINKS_TOP.map((link) => (
          <A
            key={link.to}
            className={navItemClassName(currentRoute.path.includes(link.to))}
            to={link.to}
          >
            <link.icon className={navItemIconClassName} size="20" />
            <p className={linkTextClassName}>{link.label}</p>
          </A>
        ))}
      </div>
      <div className="mb-5 flex flex-col overflow-hidden">
        {NAV_LINKS_BOTTOM.map((link) => (
          <A
            key={link.to}
            className={navItemClassName(currentRoute.path.includes(link.to))}
            to={link.to}
            onClick={link.onClick}
          >
            <link.icon className={navItemIconClassName} size="18" />
            <p className={linkTextClassName}>{link.label}</p>
          </A>
        ))}
        {!collapsed && !userLoading && (
          <div className="mt-9 flex flex-row items-center justify-between px-5 py-2.5">
            {user && (
              <div
                className="flex cursor-pointer flex-row items-center gap-2"
                onClick={() => navigate(ROUTES.profile)}
              >
                <Avatar
                  alt={t("components.header.profile")}
                  img={user?.avatar_url}
                  rounded
                />
                <div className="flex flex-col dark:text-white">
                  <p className="text-sm">{`${user?.first_name}`}</p>
                  <p className="text-xs opacity-50">{user?.username}</p>
                  <p className="text-sm">{`${team?.name}`}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
